<script>
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/vue";
import { validate } from "vee-validate";

export default {
  name: "SnsSelect",
  components: {
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
  },
  props: {
    modelValue: {
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    size: {
      type: String,
      default: "md",
    },
    label: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "Please select a value",
    },
    icon: {
      type: String,
      default: "",
    },
    validationRule: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    itemText: {
      type: String,
      default: "text",
    },
    itemValue: {
      type: String,
      default: "value",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    removable: {
      type: Boolean,
      default: false,
    },
    position: {
      type: String,
      default: "auto",
      validator: (value) => ["auto", "top", "bottom"].includes(value),
    },
  },
  emits: ["update:modelValue", "isValid", "cleared"],
  data() {
    return {
      validatorError: null,
      isOpen: false,
      shouldOpenUpward: false,
    };
  },
  // todo refactor validation
  computed: {
    selectStyle() {
      let selectClass = `select-${this.size}`;

      if (this.modelValue && this.modelValue[this.itemText]) {
        selectClass += " text-base-content";
      } else {
        selectClass += " text-neutral";
      }

      return selectClass;
    },
    dropdownPosition() {
      if (this.position === "top") {
        return "bottom-full mb-1";
      }

      if (this.position === "bottom") {
        return "top-full mt-1";
      }

      // auto mode
      return this.shouldOpenUpward ? "bottom-full mb-1" : "top-full mt-1";
    },
  },
  methods: {
    validateField() {
      if (this.validationRule !== "" || this.required) {
        validate(this.modelValue, `${this.required ? "required|" : ""}${this.validationRule}`).then((result) => {
          const isValid = result.valid && this.modelValue !== "default";

          if (isValid) {
            this.validatorError = null;
          } else if (this.modelValue === "default") {
            this.validatorError = this.$t("common.placeholder.select");
          } else {
            [this.validatorError] = result.errors;
          }

          this.$emit("isValid", isValid);
        });
      }
    },
    updateModelValue(value) {
      this.$emit("update:modelValue", value);
      this.validateField();
    },
    clearSelection() {
      const allOption = this.items.find((item) => item[this.itemValue] === "all");

      if (allOption) {
        this.updateModelValue(allOption);
        this.$emit("cleared");
      }
    },
    checkAvailableSpace() {
      if (this.position !== "auto") {
        return;
      }

      // Use a small delay to ensure the button is rendered
      setTimeout(() => {
        const button = this.$el?.querySelector("button") || this.$el?.querySelector("[data-headlessui-state]");

        if (!button) {
          return;
        }

        const buttonRect = button.getBoundingClientRect();
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        // Estimate dropdown height (max-h-96 = 24rem = ~384px)
        // Each item is approximately 48px (py-2 = 0.5rem top/bottom, px-4 padding, text height)
        const estimatedDropdownHeight = Math.min(384, this.items.length * 48 + 16);
        const minRequiredSpace = estimatedDropdownHeight + 8; // Add small buffer

        // If there's not enough space below but enough above, open upward
        if (spaceBelow < minRequiredSpace && spaceAbove >= minRequiredSpace) {
          this.shouldOpenUpward = true;
        } else if (spaceBelow >= minRequiredSpace) {
          this.shouldOpenUpward = false;
        } else {
          // Default to opening downward even if space is limited
          this.shouldOpenUpward = false;
        }
      }, 0);
    },
    handleOpen() {
      if (!this.isOpen) {
        this.isOpen = true;
        this.checkAvailableSpace();
      }
    },
    handleClose() {
      this.isOpen = false;
    },
  },
};
</script>

<template>
  <div id="sns-select" class="min-w-0">
    <Listbox
      :model-value="modelValue"
      :disabled="disabled"
      @update:model-value="updateModelValue"
      @blur="
        handleClose();
        validateField();
      "
    >
      <div class="relative">
        <ListboxButton
          class="select select-bordered w-full focus:outline-none focus:border-primary disabled:border-accent disabled:bg-base-100 disabled:opacity-70 flex items-center text-base truncate overflow-hidden min-w-0"
          :class="[selectStyle]"
          :disabled="disabled"
          @click="handleOpen"
        >
          <i v-if="icon !== ''" class="mr-2" :class="icon" />

          <div class="text-start truncate min-w-0 flex-1">
            <div class="flex select-label">
              {{ label }}
            </div>

            <div class="flex justify-between min-w-0">
              <template v-if="loading">
                <div class="basis-full">
                  <div class="flex">
                    <div class="loading loading-spinner loading-xs mr-2" />
                    {{ $t("common.loading.loading") }}
                  </div>
                </div>
              </template>

              <template v-else>
                <div class="truncate min-w-0 flex-1 mr-2">
                  {{ modelValue[itemText] || placeholder || $t("common.placeholder.select") }}
                </div>
              </template>
            </div>
          </div>

          <i
            v-if="!loading && removable && !disabled && modelValue[itemValue] && modelValue[itemValue] !== 'all'"
            class="bi-x-lg cursor-pointer shrink-0 ml-2"
            @click.stop.prevent="clearSelection"
          />
        </ListboxButton>

        <ListboxOptions
          class="absolute z-10 min-w-36 w-full max-h-96 overflow-auto rounded bg-base-100 py-1 text-base shadow-lg ring-1 ring-accent focus:outline-none sm:text-sm"
          :class="[dropdownPosition]"
        >
          <ListboxOption
            v-for="item in items"
            :key="item[itemValue]"
            v-slot="{ active, selected }"
            :value="item"
            :disabled="item.disabled"
            as="template"
          >
            <li
              :class="[
                active ? 'bg-primary !text-white' : 'text-base-content',
                'relative cursor-default select-none py-2 px-4',
              ]"
            >
              <div class="flex">
                <div class="basis-1/12 text-primary text-center mr-2" :class="active ? '!text-white' : ''">
                  <i class="bi-check" :class="selected ? 'block' : 'invisible'" />
                </div>

                <div class="basis-11/12" :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']">
                  {{ item[itemText] }}
                </div>
              </div>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </div>
    </Listbox>

    <div v-if="validatorError" class="form-text error-message">
      {{ validatorError }}
    </div>
  </div>
</template>

<style lang="scss">
#sns-select {
  font-size: 1rem;

  .error-message {
    @apply text-error;
    animation: shake-vertical 100ms ease-in-out 3;
  }

  .select-label {
    @apply text-neutral text-sm;
  }

  @keyframes shake-vertical {
    0% {
      transform: translate(1px, 0);
    }
    20% {
      transform: translate(-3px, 0);
    }
    30% {
      transform: translate(3px, 0);
    }
    40% {
      transform: translate(1px, 0);
    }
    60% {
      transform: translate(-3px, 0);
    }
    70% {
      transform: translate(3px, 0);
    }
    100% {
      transform: translate(1px, 0);
    }
  }
}
</style>
